import { doc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { parseErrorFromResponse, parseErrorType } from '../../../utils';
import { TRANSCRIPT_API_URL, AI_API_URL } from '../../../constants';
import { getLanguageByCode } from '../../../constants';
import { useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import { ERROR_TYPES } from '../../../components/Modal/ErrorModal';

import { useAnalysisError } from './useAnalysisError';
import { useAnalysisOrchestrator } from './useAnalysisOrchestrator';

export const useAnalysisProcessor = (
  userId,
  currentAnalysisId,
  setCurrentAnalysisId,
  setLoading,
  setIsStreaming,
  setSummaryCompleted,
  setChaptersCompleted,
  setStreamingChapters,
  setStreamingSummary,
  setStreamingChunkSummaries,
  setStreamingTranscript,
  setStreamingSourceLanguage,
  setAnalyzedVideoId,
  setAnalyzedTitle,
  setAnalyzedUrl,
  setProgressModal,
  setVideoDuration,
  showErrorModal,
  processTranslation,
  processChapters,
  processSummary,
  processQuestions,
  navigate
) => {
  const { t, i18n } = useTranslation();
  
  // 분리된 hook들 사용 (기존 상태들과 연결)
  const { handleAnalysisError } = useAnalysisError();
  const { orchestrateParallelProcessing, handleProcessingResults } = useAnalysisOrchestrator();

  // AbortController를 저장할 state (취소 시 사용)
  const [currentController, setCurrentController] = useState(null);
  const [currentHistoryItem, setCurrentHistoryItem] = useState(null);
  
  // 이전 값을 추적하기 위한 ref
  const previousDataRef = useRef({
    transcript: null,
    progress: null,
    chunk_summaries: null,
    summary: null,
    chapters: null,
    translatedTranscript: null
  });

  // 새로운 단순 취소 핸들러 (새로고침과 동일한 로직)
  const handleDirectCancel = async () => {
    try {
      console.log('🔴 [DEBUG] 분석 취소/확인 버튼 클릭됨!');
      console.log('🔴 [DEBUG] currentController:', currentController);
      console.log('🔴 [DEBUG] currentHistoryItem:', currentHistoryItem);
      
      // 바로 모달 닫기 (분석 완료 시 확인 버튼 또는 취소 버튼 모두)
      setProgressModal(prev => ({ ...prev, open: false }));
      console.log('✅ [DEBUG] 모달 닫기 완료');
      
      // 분석이 아직 진행 중인 경우에만 취소 로직 실행
      if (currentController && currentHistoryItem) {
        console.log('[분석 취소] 직접 취소 실행 - 새로고침과 동일한 프로세스');
        
        // 분석 취소 플래그 설정 (AbortError 처리에서 구분용)
        window.isAnalysisCancel = true;
        console.log('🔴 [DEBUG] window.isAnalysisCancel 설정됨:', window.isAnalysisCancel);
        
        // AbortController로 진행 중인 AI 처리 중단
        console.log('🔴 [DEBUG] AbortController.abort() 호출');
        currentController.abort();
      }
      
    } catch (error) {
      console.error('분석 취소 중 오류:', error);
    }
  };

  const startAnalysis = async (historyItem) => {
    // ✅ 중복 분석 방지: 이미 같은 ID로 분석 중이면 건너뛰기
    if (currentAnalysisId === historyItem.id) {
      return;
    }
    
    // ✅ 현재 분석 ID 설정
    setCurrentAnalysisId(historyItem.id);
    
    // 이전 데이터 ref 초기화
    previousDataRef.current = {
      transcript: null,
      progress: null,
      chunk_summaries: null,
      summary: null,
      chapters: null,
      translatedTranscript: null
    };
    
    setLoading(true);
    setIsStreaming(true);
    setSummaryCompleted(false);
    setChaptersCompleted(false);
    setStreamingChapters([]);
    setStreamingSummary("");
    setStreamingChunkSummaries([]);
    setStreamingTranscript("");
    
    // 진행률 모달 초기화 및 열기 (취소 핸들러 포함)
    console.log('🔴 [DEBUG] ProgressModal 설정 중, handleDirectCancel:', typeof handleDirectCancel);
    
    setProgressModal({
      open: true,
      currentMessage: t('analyze.analysisProgress'),
      onCancel: handleDirectCancel,
      userId: userId,
      historyId: historyItem.id
    });

    // 분석 시작 시간 기록
    const analysisStartTime = Date.now();
    
    // AbortController 설정 (API 호출 취소용)
    const controller = new AbortController();
    const signal = controller.signal;

    // 현재 controller와 historyItem 저장 (취소 시 사용)
    setCurrentController(controller);
    setCurrentHistoryItem(historyItem);
    
    // ✅ 전역에서 접근 가능하도록 controller 저장
    window.currentAnalysisController = controller;

    setAnalyzedVideoId(historyItem.videoId);
    setAnalyzedTitle(historyItem.title);
    setAnalyzedUrl(historyItem.url);

    try {
      // ✅ 백엔드 분석 시작
      console.log('🚀 [백엔드 분석] 시작');
      
      // 🔥 15초 타임아웃 체크 (YouTube 자막 추출 지연 감지)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('YOUTUBE_TIMEOUT:408'));
        }, 15000); // 15초 타임아웃
      });

      const responsePromise = fetch(`${AI_API_URL}/api/analyze/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          url: historyItem.url,
          user_id: userId,
          language: i18n.language,
          history_id: historyItem.id
        }),
        signal: signal
      });

      const response = await Promise.race([responsePromise, timeoutPromise]);
      
      if (!response.ok) {
        // 409 에러 (분석 진행 중) 특별 처리
        if (response.status === 409) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { detail: errorText };
          }
          
          const errorDetail = errorData.detail || errorText;
          console.log('🔄 [분석 중복] 409 에러 감지:', errorDetail);
          
          // 에러 메시지에서 히스토리 ID 추출 (형식: "메시지|히스토리ID")
          if (errorDetail.includes('|')) {
            const [message, runningHistoryId] = errorDetail.split('|');
            throw new Error(`ANALYSIS_IN_PROGRESS:409:${runningHistoryId}`);
          } else {
            throw new Error(`ANALYSIS_IN_PROGRESS:409`);
          }
        }
        
        const errorType = await parseErrorFromResponse(response);
        throw new Error(`${errorType}:${response.status}`);
      }
      
      const data = await response.json();
      const { history_id } = data;
      
      console.log(`🚀 [Phase 2] 백그라운드 분석 시작됨 - history_id: ${history_id}`);
      
      // Firebase 리스너 설정
      const docRef = doc(db, "users", userId, "history", historyItem.id);
      
      // Firebase 실시간 리스너 설정 (Phase 2: 모든 데이터 실시간 업데이트)
      const unsubscribe = onSnapshot(docRef, (doc) => {
        console.log('🔥 [Firebase Listener] 이벤트 발생');
        
        if (doc.exists()) {
          const data = doc.data();
          console.log('📋 [Firebase Data]', {
            transcript: data.transcript ? `${data.transcript.substring(0, 100)}...` : null,
            progress: data.processingProgress,
            status: data.status,
            sourceLanguage: data.sourceLanguage,
            backgroundProcessing: data.backgroundProcessing
          });
          
          const prevData = previousDataRef.current;
          
          // 자막 업데이트 (실제로 변경된 경우만)
          if (data.transcript && data.transcript !== prevData.transcript) {
            console.log('✅ [Firebase] 자막 업데이트 - 길이:', data.transcript.length);
            setStreamingTranscript(data.transcript);
            previousDataRef.current.transcript = data.transcript;
            setLoading(false); // 자막이 준비되면 로딩 종료
          }
          
          // 원본 언어 정보
          if (data.sourceLanguage) {
            setStreamingSourceLanguage(data.sourceLanguage);
            console.log(`🔍 [Firebase] 원본 언어: ${data.sourceLanguage}`);
          }
          
          // 진행률 업데이트 (실제로 변경된 경우만)
          const currentProgress = data.processingProgress;
          if (currentProgress !== undefined && currentProgress !== prevData.progress) {
            console.log(`📊 [Firebase] 진행률 업데이트: ${prevData.progress}% → ${currentProgress}%`);
            setProgressModal(prev => ({
              ...prev,
              progress: currentProgress,
              currentMessage: prev.currentMessage, // 메시지는 유지
              userId: userId,
              historyId: historyItem.id
            }));
            previousDataRef.current.progress = currentProgress;
          }
          
          // 청크 요약 업데이트 (배열 길이가 다른 경우만)
          if (data.chunk_summaries && data.chunk_summaries.length !== (prevData.chunk_summaries?.length || 0)) {
            setStreamingChunkSummaries(data.chunk_summaries);
            previousDataRef.current.chunk_summaries = data.chunk_summaries;
          }
          
          // 최종 요약 업데이트 (실제로 변경된 경우만)
          if (data.summary && data.summary !== prevData.summary) {
            setStreamingSummary(data.summary);
            setSummaryCompleted(true);
            previousDataRef.current.summary = data.summary;
          }
          
          // 챕터 업데이트 (배열 길이가 다른 경우만)
          if (data.chapters && data.chapters.length > 0 && data.chapters.length !== (prevData.chapters?.length || 0)) {
            setStreamingChapters(data.chapters);
            setChaptersCompleted(true);
            previousDataRef.current.chapters = data.chapters;
          }
          
          // 질문 업데이트 (백엔드에서 처리되므로 여기서는 감지만)
          if (data.questions && data.questions.length > 0) {
            console.log('✅ [Firebase] 추천 질문 감지:', data.questions.length + '개');
          }
          
          // 번역된 자막 업데이트
          if (data.translatedTranscript && data.translatedTranscript !== prevData.translatedTranscript) {
            console.log('🌐 [Firebase] 번역 자막 업데이트 - 길이:', data.translatedTranscript.length);
            // 번역이 완료되면 번역된 자막으로 교체
            if (data.translatedLanguage && data.translatedLanguage !== data.sourceLanguage) {
              setStreamingTranscript(data.translatedTranscript);
              previousDataRef.current.translatedTranscript = data.translatedTranscript;
            }
          }
          
          // 백그라운드 처리 완료 감지
          if (data.status === 'active' && data.backgroundProcessing === false) {
            console.log('🎉 [Firebase] 백그라운드 처리 완료!');
            setIsStreaming(false);
            setProgressModal(prev => ({ 
              ...prev, 
              progress: 100,
              currentMessage: t('analyze.analysisCompleteMessage'),
              userId: userId,
              historyId: historyItem.id
            }));
            
            // 분석 시간 로깅
            const totalTime = (Date.now() - analysisStartTime) / 1000;
            console.log(`🎉 전체 분석 완료! 총 소요시간: ${totalTime.toFixed(1)}초`);
            
            // 광역 히스토리 복사는 백엔드에서 자동으로 처리됨
            console.log('🌍 [광역 저장] 백엔드에서 자동으로 처리됨');
            
            // 🎯 모달 자동 닫힘 제거 - 사용자가 확인 버튼을 누를 때까지 유지
          }
          
          // 에러 감지
          if (data.status === 'error' && data.error) {
            console.log('❌ [Firebase] 분석 에러 감지:', data.error);
            
            // 진행률 모달 닫기
            setProgressModal(prev => ({ ...prev, open: false }));
            
            // 에러 타입 결정
            let errorType = ERROR_TYPES.UNKNOWN_ERROR;
            if (data.error.includes('자막이 없') || data.error.includes('사용 가능한 자막이 없')) {
              errorType = ERROR_TYPES.NO_CAPTIONS;
            } else {
              errorType = parseErrorType(data.error);
            }
            
            // 에러 모달 표시
            showErrorModal(
              errorType,
              '',
              () => startAnalysis(historyItem),
              historyItem.id
            );
            
            // 상태 정리
            setLoading(false);
            setIsStreaming(false);
            setCurrentAnalysisId(null);
            
            // Firebase 리스너 정리
            if (window.currentUnsubscribe) {
              window.currentUnsubscribe();
              window.currentUnsubscribe = null;
            }
            
            return; // 더 이상 처리하지 않음
          }
        }
      });
      
      // unsubscribe 함수를 저장해서 cleanup 시 사용
      window.currentUnsubscribe = unsubscribe;

    } catch (error) {
      // ✅ AbortError인 경우 처리
      if (error.name === 'AbortError') {
        console.log('🔴 [DEBUG] AbortError 감지됨!');
        
        // 분석 취소인 경우
        if (window.isAnalysisCancel) {
          console.log('⚠️ [분석 취소] 사용자가 분석을 취소했습니다');
          // Phase 2에서는 백엔드가 상태를 관리하므로 별도 처리 불필요
        }
        
        setProgressModal(prev => ({ ...prev, open: false }));
        setLoading(false);
        setCurrentAnalysisId(null);
        
        // 분석 취소인 경우
        if (window.isAnalysisCancel) {
          window.isAnalysisCancel = false;
        }
        
        return;
      }
      
      await handleAnalysisError(
        error,
        historyItem,
        userId,
        showErrorModal,
        startAnalysis,
        setProgressModal,
        setLoading,
        setCurrentAnalysisId
      );
    } finally {
      setLoading(false);
      // ✅ 분석 완료 시 상태 초기화
      setCurrentAnalysisId(null);
      setCurrentController(null);
      setCurrentHistoryItem(null);
      // ✅ 전역 controller 정리
      if (window.currentAnalysisController) {
        window.currentAnalysisController = null;
      }
      // Firestore 리스너 정리
      if (window.currentUnsubscribe) {
        window.currentUnsubscribe();
        window.currentUnsubscribe = null;
      }
    }
  };

  return {
    startAnalysis,
    handleDirectCancel
  };
}; 